import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import { andThen, any, anyPass, assoc, compose, defaultTo, dissoc, equals, filter, has, isNil, length, lensProp, map, over, path, pathOr, prop, propEq, propOr, when, isNotEmpty } from 'ramda';
import { isNilOrEmpty, isNotNilOrEmpty, notEqual } from 'ramda-adjunct';
import { renderToStaticMarkup } from 'react-dom/server';
import useFetchQuery from '../../../../hooks/useFetchQuery';
import { QWidgetQuery } from '../../../../_graphql/queries/documents/QWidget';
import Loading from '../../../../components/Loading';
import '@fasstech/flexiget/dist/css/main.css';
import '@fasstech/flexiget-mme/dist/css/main.css';
import '@fasstech/flexiget-april/dist/css/main.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { getElementByVersion, getFlexigetElements } from '../helpers/flexigets';
import { saveImages } from '../../../../hooks/useWidget';
import WidgetFormCore from '../widget/WidgetFormCore';
import { normalizeVariables } from '../../../helpers/normalizeVariables';
import { propsAreDefined } from '../../../../hooks/useRules';

const RepriseWidgetModal = ({ widgetIdEditing, onClose, updateWidget, teamId, variables, scale, env, offerId }) => {
  const { widget, isLoading } = useFetchQuery({
    query: QWidgetQuery,
    args: {
      widgetId: widgetIdEditing,
      fromTeamId: teamId,
      variables: normalizeVariables(defaultTo({}, variables))
    },
    dataProp: 'widget',
    defaultData: {},
    env
  });

  const flexigets = ['flexigetDefault'];

  const [state, setState] = useState({ values: widget, loading: false });
  const [elements, setElements] = useState(getFlexigetElements(flexigets));
  const prevContents = useRef({});

  useEffect(() => {
    if (isNotNilOrEmpty(widget)) {
      const parsedWidget = {
        ...widget,
        contents: JSON.parse(prop('contents', widget))
      };
      setState(assoc('values', parsedWidget));
    }
  }, [widget]);

  useEffect(() => {
    const contents = pathOr([], ['values', 'contents'], state);
    if (notEqual(prevContents.current, contents)) {
      map((element) => {
        const Component = getElementByVersion(prop('type', element), propOr('1.0', 'version', element)).Component;
        setElements(assoc(prop('componentName', Component), Component, elements));
      })(contents);
      prevContents.current = contents;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop('values', state)]);

  const onSave = async () => {
    setState(assoc('loading', true));
    const processImages = async (values) => {
      const hasImage = compose(
        any(propEq('image', 'type')),
        prop('contents')
      )(values);

      if (hasImage) {
        const contents = await saveImages(values);
        if (any(isNil)(contents)) {
          setState(assoc('loading', false));
          return null;
        }
        return { ...values, contents };
      }
      return values;
    };
    const updateHtmlContent = (values) => {
      when(
        (elementsFlexiget) => equals(length(path(['contents'], values)), length(elementsFlexiget)),
        map((element, key) => {
          if (equals('childrens', prop('type', element))) element.type = 'children';

          const ElementFlexiget = getElementByVersion(prop('type', element), propOr('1.0', 'version', element)).Component;
          const html = path(['element', 0, 'html'], element);
          const elementGetHtml = isNotNilOrEmpty(html);

          const content = elementGetHtml ? html : renderToStaticMarkup(<ElementFlexiget value={prop('element', element)} key={key} />);
          element.htmlContent = content;

          const elementContentSlate = compose(
            map(
              compose(
                dissoc('children'),
                when(
                  (element) => anyPass([equals('paragraph'), equals('signature'), equals('checkbox')])(prop('type', element)),
                  assoc('type', 'text')
                )
              )
            ),
            filter(has('children'))
          )(prop('element', element));
          if (isNotEmpty(elementContentSlate)) element.element = elementContentSlate;
        })
      )(prop('contents', values));

      return compose(
        over(
          lensProp('contents'),
          JSON.stringify
        )
      )(values);
    };

    const values = await compose(
      andThen(updateHtmlContent),
      async (values) => processImages(values),
      propOr({}, 'values')
    )(state);

    const removeLoading = () => setState(assoc('loading', false));
    updateWidget(values, removeLoading);
  };
  const isValid = propsAreDefined(['name', 'scope', 'contents'])(prop('values', state));

  return (
    <Modal
      open={!isNil(widgetIdEditing)}
      footer={null}
      onCancel={onClose}
      width="100%"
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      centered
      className="!max-w-full"
      closable
    >
      {
        isNilOrEmpty(prop('values', state))
          ? isLoading
            ? <Loading />
            : (
              <div>
                {'Le widget n\'existe pas.'}
                <Button onClick={onClose}>Fermer</Button>
              </div>
            )
          : (
            <WidgetFormCore
              modalMode
              offerId={offerId}
              values={prop('values', state)}
              isEdit
              isLateEdit
              scale={scale}
              elements={elements}
              flexigets={flexigets}
              teamId={teamId}
              setState={setState}
              onCancel={onClose}
              onSaveAndQuit={onSave}
              loading={prop('loading', state)}
              isValid={isValid}
              userCanEdit
            />
          )
      }
    </Modal>
  );
};

export default RepriseWidgetModal;
